/**
 * HeroSection Component - Enhanced Version
 * =========================================
 * Main hero content with particle background, logo assembly, and CTAs
 * 
 * New Features:
 * - Prominent "DRAVONIX" title with light sweep effect
 * - Restructured tagline (ENGINEERING THE + DECENTRALIZED FUTURE)
 * - Service tags
 * - Dual CTAs (Talk to Engineer + Explore Services)
 * - WhatsApp floating orb
 * - Enhanced ease hierarchy
 * - Micro-motion effects
 */

import React, { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import ParticleCanvas from './ParticleCanvas';

// Import the logo
import logo from '../../assets/dravonix_icon.png';

const HeroSection = ({ phase, onPhaseComplete, isVisible }) => {
  const containerRef = useRef(null);
  const energyPointRef = useRef(null);
  const energyPulseRef = useRef(null);
  const logoContainerRef = useRef(null);
  const logoRef = useRef(null);
  const logoGlowRef = useRef(null);
  const fragmentsRef = useRef(null);
  const brandNameRef = useRef(null);
  const lightSweepRef = useRef(null);
  const taglineSmallRef = useRef(null);
  const taglineBigRef = useRef(null);
  const serviceTagsRef = useRef(null);
  const ctaPrimaryRef = useRef(null);
  const ctaSecondaryRef = useRef(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isMobile, setIsMobile] = useState(false);

  // WhatsApp number
  const WHATSAPP_NUMBER = '919962461250';

  // Check for mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Mouse parallax effect (desktop only) - Depth Illusion
  useEffect(() => {
    if (isMobile || phase !== 'complete') return;

    const handleMouseMove = (e) => {
      const x = (e.clientX / window.innerWidth - 0.5) * 2;
      const y = (e.clientY / window.innerHeight - 0.5) * 2;
      setMousePosition({ x, y });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [isMobile, phase]);

  // Apply parallax with depth layers
  useEffect(() => {
    if (isMobile || !logoContainerRef.current) return;

    // Logo moves medium
    gsap.to(logoContainerRef.current, {
      x: mousePosition.x * 15,
      y: mousePosition.y * 15,
      duration: 0.8,
      ease: 'power2.out',
    });

    // Text moves least
    gsap.to([brandNameRef.current, taglineSmallRef.current, taglineBigRef.current], {
      x: mousePosition.x * 5,
      y: mousePosition.y * 5,
      duration: 1,
      ease: 'power2.out',
    });
  }, [mousePosition, isMobile]);

  // Hero reveal animation with enhanced ease hierarchy
  useEffect(() => {
    if (phase !== 'hero' && phase !== 'complete') return;

    // If already complete (returning user), show immediately
    if (phase === 'complete') {
      gsap.set([
        logoRef.current,
        brandNameRef.current,
        taglineSmallRef.current,
        taglineBigRef.current,
        serviceTagsRef.current,
        ctaPrimaryRef.current,
        ctaSecondaryRef.current
      ], { opacity: 1 });
      gsap.set(logoRef.current, { scale: 1 });
      gsap.set('.brand-letter', { opacity: 1, y: 0 });
      gsap.set('.tagline-big-word', { opacity: 1, y: 0, scale: 1 });
      gsap.set('.cta-primary-border', { strokeDashoffset: 0 });
      gsap.set('.cta-secondary-border', { strokeDashoffset: 0 });
      startContinuousAnimations();
      return;
    }

    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        onComplete: () => {
          onPhaseComplete('hero');
          startContinuousAnimations();
        },
      });

      // Initial states
      gsap.set([energyPointRef.current, energyPulseRef.current, logoRef.current], {
        opacity: 0,
        scale: 0,
      });
      gsap.set([
        brandNameRef.current,
        taglineSmallRef.current,
        taglineBigRef.current,
        serviceTagsRef.current,
        ctaPrimaryRef.current,
        ctaSecondaryRef.current
      ], {
        opacity: 0,
        y: 30,
      });

      // Create blockchain fragments
      createFragments();

      // ===== ANIMATION SEQUENCE =====

      // 1. Energy point appears (expo.out)
      tl.to(energyPointRef.current, {
        opacity: 1,
        scale: 1,
        duration: 0.4,
        ease: 'expo.out',
      });

      // 2. Energy pulse expands (expo.out)
      tl.to(
        energyPulseRef.current,
        {
          opacity: 0.8,
          scale: 1,
          duration: 0.5,
          ease: 'expo.out',
        },
        '-=0.2'
      );

      // 3. Pulse ripple effect
      tl.to(energyPulseRef.current, {
        scale: 4,
        opacity: 0,
        duration: 1,
        ease: 'expo.out',
      });

      // 4. Fragments fly in and assemble (power4.out)
      tl.to(
        '.blockchain-fragment',
        {
          x: 0,
          y: 0,
          opacity: 1,
          scale: 1,
          rotation: 0,
          duration: 1,
          stagger: {
            each: 0.02,
            from: 'random',
          },
          ease: 'power4.out',
        },
        '-=0.6'
      );

      // 5. Fragments fade out as logo appears
      tl.to(
        '.blockchain-fragment',
        {
          opacity: 0,
          duration: 0.4,
          ease: 'power2.out',
        },
        '-=0.3'
      );

      // 6. Logo reveal with settle (power2.out)
      tl.to(
        logoRef.current,
        {
          opacity: 1,
          scale: 1,
          duration: 0.8,
          ease: 'power2.out',
        },
        '-=0.4'
      );

      // 7. Energy point fades
      tl.to(
        energyPointRef.current,
        {
          opacity: 0,
          duration: 0.4,
          ease: 'power2.out',
        },
        '-=0.5'
      );

      // 8. Brand name "DRAVONIX" appears (power1.out)
      tl.to(
        brandNameRef.current,
        {
          opacity: 1,
          y: 0,
          duration: 0.6,
          ease: 'power1.out',
        },
        '-=0.2'
      );

      // 9. Animate individual brand letters
      tl.fromTo(
        '.brand-letter',
        {
          opacity: 0,
          y: 40,
          rotationX: -90,
        },
        {
          opacity: 1,
          y: 0,
          rotationX: 0,
          duration: 0.5,
          stagger: 0.06,
          ease: 'power2.out',
        },
        '-=0.4'
      );

      // 10. Light sweep across brand name
      tl.fromTo(
        lightSweepRef.current,
        {
          left: '-100%',
        },
        {
          left: '200%',
          duration: 0.8,
          ease: 'power2.inOut',
        },
        '-=0.2'
      );

      // 11. Small tagline "ENGINEERING THE" (power1.out)
      tl.to(
        taglineSmallRef.current,
        {
          opacity: 1,
          y: 0,
          duration: 0.5,
          ease: 'power1.out',
        },
        '-=0.3'
      );

      // 12. Big tagline "DECENTRALIZED FUTURE" with word animation
      tl.to(
        taglineBigRef.current,
        {
          opacity: 1,
          y: 0,
          duration: 0.6,
          ease: 'power1.out',
        },
        '-=0.2'
      );

      tl.fromTo(
        '.tagline-big-word',
        {
          opacity: 0,
          y: 30,
          scale: 0.9,
        },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 0.6,
          stagger: 0.15,
          ease: 'power2.out',
        },
        '-=0.4'
      );

      // 13. Service tags
      tl.to(
        serviceTagsRef.current,
        {
          opacity: 1,
          y: 0,
          duration: 0.5,
          ease: 'power1.out',
        },
        '-=0.2'
      );

      // 14. Primary CTA with delay (back.out)
      tl.to(
        ctaPrimaryRef.current,
        {
          opacity: 1,
          y: 0,
          duration: 0.6,
          ease: 'back.out(1.2)',
        },
        '+=0.3' // 300ms delay after tagline
      );

      // 15. CTA border draw
      tl.fromTo(
        '.cta-primary-border',
        { strokeDashoffset: 600 },
        { strokeDashoffset: 0, duration: 0.8, ease: 'power2.inOut' },
        '-=0.4'
      );

      // 16. Secondary CTA
      tl.to(
        ctaSecondaryRef.current,
        {
          opacity: 1,
          y: 0,
          duration: 0.5,
          ease: 'back.out(1.2)',
        },
        '-=0.4'
      );

      // 16b. Secondary CTA border draw
      tl.fromTo(
        '.cta-secondary-border',
        { strokeDashoffset: 500 },
        { strokeDashoffset: 0, duration: 0.7, ease: 'power2.inOut' },
        '-=0.3'
      );

    }, containerRef);

    return () => ctx.revert();
  }, [phase, onPhaseComplete, isMobile]);

  // Continuous micro-motion animations
  const startContinuousAnimations = () => {
    // Logo float
    if (logoRef.current) {
      gsap.to(logoRef.current, {
        y: -12,
        duration: 2.5,
        repeat: -1,
        yoyo: true,
        ease: 'power1.inOut',
      });
    }

    // Logo glow intensity shift
    if (logoGlowRef.current) {
      gsap.to(logoGlowRef.current, {
        opacity: 0.7,
        scale: 1.15,
        duration: 2,
        repeat: -1,
        yoyo: true,
        ease: 'power1.inOut',
      });
    }
  };

  // Create blockchain fragments
  const createFragments = () => {
    if (!fragmentsRef.current) return;

    const fragmentCount = isMobile ? 20 : 50;
    fragmentsRef.current.innerHTML = '';

    for (let i = 0; i < fragmentCount; i++) {
      const fragment = document.createElement('div');
      fragment.className = 'blockchain-fragment';

      const angle = (i / fragmentCount) * Math.PI * 2;
      const distance = gsap.utils.random(200, 450);
      const startX = Math.cos(angle) * distance;
      const startY = Math.sin(angle) * distance;

      fragment.style.cssText = `
        left: 50%;
        top: 28%;
        width: ${gsap.utils.random(6, 18)}px;
        height: ${gsap.utils.random(6, 18)}px;
      `;

      gsap.set(fragment, {
        x: startX,
        y: startY,
        rotation: gsap.utils.random(0, 360),
        scale: gsap.utils.random(0.5, 1.5),
        opacity: 0,
      });

      fragmentsRef.current.appendChild(fragment);
    }
  };

  // WhatsApp handlers
  const handleWhatsAppClick = () => {
    const message = encodeURIComponent('Hi! I\'m interested in Dravonix blockchain development services.');
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${message}`, '_blank');
  };

  const handleExploreClick = () => {
    // Will scroll to services section later
    const servicesSection = document.getElementById('services');
    if (servicesSection) {
      servicesSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Brand name letters
  const brandText = 'DRAVONIX';
  const brandLetters = brandText.split('').map((letter, index) => (
    <span key={index} className="brand-letter">
      {letter}
    </span>
  ));

  // Big tagline words
  const bigTaglineWords = ['DECENTRALIZED', 'FUTURE'];

  return (
    <div
      className={`hero-section ${isVisible ? 'visible' : ''}`}
      ref={containerRef}
    >
      {/* Particle Background */}
      <ParticleCanvas isActive={isVisible} isMobile={isMobile} mousePosition={mousePosition} />

      {/* Grid overlay */}
      <div className="hero-grid"></div>

      {/* Energy Point */}
      <div className="energy-point" ref={energyPointRef}></div>

      {/* Energy Pulse */}
      <div className="energy-pulse" ref={energyPulseRef}></div>

      {/* Blockchain Fragments Container */}
      <div className="fragments-container" ref={fragmentsRef}></div>

      {/* Main Content Container */}
      <div className="hero-content">
        {/* Logo Container */}
        <div className="hero-logo-container" ref={logoContainerRef}>
          <div className="hero-logo-glow" ref={logoGlowRef}></div>
          <div className="hero-logo" ref={logoRef}>
            <img
              src={logo}
              alt="Dravonix Dragon"
              className="hero-logo-image"
            />
          </div>
        </div>

        {/* Brand Name - DRAVONIX */}
        <h1 className="brand-name" ref={brandNameRef}>
          <span className="brand-text">{brandLetters}</span>
          <span className="light-sweep" ref={lightSweepRef}></span>
        </h1>

        {/* Tagline - Small */}
        <p className="tagline-small" ref={taglineSmallRef}>
          ENGINEERING THE
        </p>

        {/* Tagline - Big */}
        <h2 className="tagline-big" ref={taglineBigRef}>
          {bigTaglineWords.map((word, index) => (
            <span key={index} className="tagline-big-word">
              {word}
            </span>
          ))}
        </h2>

        {/* Service Tags */}
        <div className="service-tags" ref={serviceTagsRef}>
          <span>Blockchain</span>
          <span className="tag-separator">•</span>
          <span>Smart Contracts</span>
          <span className="tag-separator">•</span>
          <span>Software</span>
          <span className="tag-separator">•</span>
          <span>Digital Growth</span>
        </div>

        {/* CTAs Container */}
        <div className="cta-container">
          {/* Primary CTA */}
          <div className="cta-primary" ref={ctaPrimaryRef}>
            <button className="cta-button-primary" onClick={handleWhatsAppClick}>
              <svg className="cta-border-svg" viewBox="0 0 300 60" preserveAspectRatio="none">
                <rect
                  className="cta-primary-border"
                  x="1" y="1" width="298" height="58" rx="30"
                  fill="none" stroke="url(#ctaGradientPrimary)" strokeWidth="2"
                  strokeDasharray="600"
                />
                <defs>
                  <linearGradient id="ctaGradientPrimary" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#D4A853" />
                    <stop offset="50%" stopColor="#F5D78E" />
                    <stop offset="100%" stopColor="#D4A853" />
                  </linearGradient>
                </defs>
              </svg>
              <span className="cta-text-primary">Talk to a Dravonix Engineer</span>
              <span className="cta-whatsapp-icon">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
              </span>
            </button>
          </div>

          {/* Secondary CTA - Same style as Primary */}
          <div className="cta-secondary" ref={ctaSecondaryRef}>
            <button className="cta-button-secondary" onClick={handleExploreClick}>
              <svg className="cta-border-svg" viewBox="0 0 240 60" preserveAspectRatio="none">
                <rect
                  className="cta-secondary-border"
                  x="1" y="1" width="238" height="58" rx="30"
                  fill="none" stroke="url(#ctaGradientSecondary)" strokeWidth="2"
                  strokeDasharray="500"
                />
                <defs>
                  <linearGradient id="ctaGradientSecondary" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#888888" />
                    <stop offset="50%" stopColor="#AAAAAA" />
                    <stop offset="100%" stopColor="#888888" />
                  </linearGradient>
                </defs>
              </svg>
              <span className="cta-text-secondary">Explore Services</span>
              <span className="cta-arrow">→</span>
            </button>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="scroll-indicator">
        <div className="scroll-mouse">
          <div className="scroll-wheel"></div>
        </div>
        <span className="scroll-text">Scroll to explore</span>
      </div>
    </div>
  );
};

export default HeroSection;
